import { objectType } from "_src/constants";
import Selector from "_src/client/manipulators/selector";
import workspace from "_src/workspace";
import output from "_src/engine/output";
import appState from "_src/app_state";
import {BufferReaderType} from "_src/client/buffer_reader";
import windowManager from "_src/client/window_manager";
import {screenUtils} from "_src/client/utils";
import { vec3, quat, mat4 } from "_src/libs/gl-matrix";
import events from "_src/engine/events";
import Dimensions from "_src/client/widgets/dimensions";
import textWidget from "_src/client/widgets/text"
import loader from "_src/loader";
import containersCache from "_src/engine/packers/containers_cache";
import reformer from "_src/reformer/reformer";

const RADIUS_PICK_ANNOTATION_ANGLE = Math.PI / 10;

export default class CadFitter extends Selector {

  constructor(camera) {
    super(camera);

    this._pickAnnotationLines = null;
    this._pickAnnotationText = null;
  }

  _onStart() {
    this._storedSteps.length = 0;
    this.resetPickingMode();
  }

  _onStop() {
    this._updateCurrentPickAnnotation(null);
  }

  resetPickingMode() {
    const selectionType = appState.getStateValue("selectionType");
    const pickingMode = appState.getStateValue("cadFitterPickingMode");
    this.setSelectionType(pickingMode ? appState.ST_NONE : selectionType);
  }

  _mouseUpHandler({screenX, screenY}) {
    const pickingMode = appState.getStateValue("cadFitterPickingMode");
    if (pickingMode) {
      this._pickCad(screenX, screenY);
    } else {
      super._mouseUpHandler();
    }
  }

  _updateScale(camera) {
    if (!this._pickAnnotationText) {
      return;
    }

    textWidget.updateScreenAppearance(this._pickAnnotationText, camera);
  }

  _cameraMoveHandler(camera) {
    if (camera !== this._camera) {
      return;
    }

    this._updateScale(camera);
  }

  async _pickCad(screenX, screenY) {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    const pickID = await mainWindow.bufferReader.readPixelCache(screenX, screenY, BufferReaderType.OBJECT_ID);
    const object = workspace.getObjectByID(pickID);
    if (!object || object.type !== objectType.CAD) {
      this._updateCurrentPickAnnotation(null);
      return;
    }

    const {x, y} = screenUtils.toScreen(mainWindow.getViewport(), this._camera.viewport, screenX, screenY);
    const cameraOrigin = this._camera.calculateOrigin(x, y, vec3.create());
    const cameraRay = this._camera.calculateRay(x, y, vec3.create());
    const toCadLocalMat = mat4.invert(mat4.create(), object.model.modelMatrix);
    const toCadLocalQuat = quat.invert(quat.create(), object.model.quat);
    const cadLocalCameraOrigin = vec3.transformMat4(vec3.create(), cameraOrigin, toCadLocalMat);
    const cadLocalCameraRay = vec3.transformQuat(vec3.create(), cameraRay, toCadLocalQuat);

    const payload = {
      "objectUuid": object.getUUID(),
      "origin": Array.from(cadLocalCameraOrigin),
      "direction": Array.from(cadLocalCameraRay),
      "slices": [workspace.getCadSlice(object)],
      "projectFiles": [],
    };

    workspace.getObjectFiles(object, payload["projectFiles"]);
    events.emit("pickCad", payload);
  }

  applyPickCad(payload) {
    const object = workspace.getObjectByUUID(payload["uuid"]);
    if (!object) {
      output.error(output.API_ERROR, "invalid cad object uuid: " + payload["uuid"]);
      return;
    }

    this._updateCurrentPickAnnotation(payload);
    if (!this._pickAnnotationText) {
      return;
    }

    this._pickAnnotationText.setMatrix(object.model.modelMatrix);
    this._pickAnnotationLines.setMatrix(object.model.modelMatrix);
    this._pickAnnotationText.updateMatrix();

    this._updateScale(this._camera);
  }

  async applyFitMask(object) {
    if (object.type !== objectType.FUSION) {
      output.error(output.DEVELOPERS_ERROR, "trying to apply fit mask to non-fusion object");
      return;
    }

    const childrenCads = [];
    for (const cadObject of workspace.getObjects().filter(object => object.type === objectType.CAD)) {
      if (cadObject.getProperty("referencedFusion") === object.getUUID()) {
        childrenCads.push(cadObject);
      }
    }
    for (const chunk of object.frames[0].model.chunks) {
      chunk.updateSelectionMask(true);
    }

    for (const cadObject of childrenCads) {
      const chunkToFitMaskGeometry = await loadBuffersForObject(object, cadObject.getProperty("fitMaskFrames")[0].getValue("geometries"));
      for (const [chunk, fitMaskGeometry] of chunkToFitMaskGeometry.entries()) {
        const selectionMask = chunk.geometry.getLayoutItem("a_selection_mask").getBuffer();
        const fitMask = fitMaskGeometry.getLayoutItem("fit_mask").getBuffer();

        if (selectionMask.length !== fitMask.length) {
          output.error(output.DEVELOPERS_ERROR, "can't apply fit mask: chunk size is differ");
          continue;
        }

        for (let i=0; i<fitMask.length; i++) {
          if (fitMask[i]) {
            selectionMask[i] = 1;
          }
        }

        chunk.markAttributeDirty("a_selection_mask");
      }
    }

    this.setupRenderingProperties(object);
  }

  setupRenderingProperties(object) {
    object.model.shader.setDirective("SELECTION", true);
  }

  //Reimplemented
  _checkCanSelect(object) {
    const pickingMode = appState.getStateValue("cadFitterPickingMode");
    return !pickingMode && object.type === objectType.FUSION && super._checkCanSelect(object);
  }

  _updateCurrentPickAnnotation(metadata) {
    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const workspaceInterfaceScene = mainRenderGraph.getInterfaceScene();
    mainRenderGraph.requestFrame();

    workspaceInterfaceScene.remove(this._pickAnnotationLines);
    workspaceInterfaceScene.remove(this._pickAnnotationText);
    this._pickAnnotationLines = null;
    this._pickAnnotationText = null;

    if (!metadata) {
      return;
    }

    switch(metadata.element) {
      case "axis": {
        const height = vec3.distance(metadata.axis[0], metadata.axis[1]);
        const text = "h=" + height.toFixed(2);
        const params = {lpoint: metadata.axis[0], rpoint: metadata.axis[1], offset: 2 * metadata.radius, text: text};
        [this._pickAnnotationLines, this._pickAnnotationText] = Dimensions.create(Dimensions.DL_LINEAR, params);

        break;
      }
      case "radius": {
        const text = "r=" + metadata.radius.toFixed(2);
        const params = {lpoint: metadata.axis[0], rpoint: metadata.axis[1], offset: 2 * metadata.radius,
          text: text, angle: RADIUS_PICK_ANNOTATION_ANGLE, idx: metadata.pointIdx};
        [this._pickAnnotationLines, this._pickAnnotationText] = Dimensions.create(Dimensions.DL_ANGULAR, params);

        break;
      }
      default:
        return;
    }

    workspaceInterfaceScene.add(this._pickAnnotationLines);
    workspaceInterfaceScene.add(this._pickAnnotationText);
  }

}

async function loadBuffersForObject(object, chunkProperties) {

  const chunkToBuffer = new Map();
  const objectID = object.getID();
  const frameID = object.frames[0].getID();

  const loadingPromises = [];
  const loadingFiles = new Set();
  for (const chunkProperty of chunkProperties) {
    const payload = {
      objectID: objectID,
      frameID: frameID,
      id: chunkProperty["index"],
    };

    const file = reformer.getProjectDirectory() + chunkProperty["file"];
    loadingFiles.add(file);
    loadingPromises.push(loader.load(file, loader.GEOMETRY, payload));
  }
  const geometries = await Promise.all(loadingPromises);
  for (const file of loadingFiles) {
    containersCache.releaseContainer(file);
  }

  for (let i=0; i<geometries.length; i++) {
    const geometry = geometries[i];

    const objectChunkProperty = object.getProperty("frames")[0].getValue("geometries")[i];
    if (!objectChunkProperty) {
      continue;
    }
    const chunkID = objectChunkProperty.getValue("id");

    const objectChunk = object.frames[0].model.chunks.find(chunk => chunk.getID() === chunkID);
    if (!objectChunk) {
      output.warn("Can't add distance map to chunk: " + chunkID + ". Chunk already deleted.");
      continue;
    }
    chunkToBuffer.set(objectChunk, geometry);
  }

  return chunkToBuffer;
}
